// ============================================================================
// EVIDENCE CONCEPTS — aggregate (Phase L-1)
//
// Concatenates the per-specialty data modules into the single frozen registry,
// owns the ORDERED recognition tables (precedence is a global concern), and
// exposes the read helpers + recognition/projection API. The facade
// (evidenceConceptLibrary.js) re-exports this module's public surface verbatim,
// so every existing importer keeps working unchanged.
//
//   conceptBuilder.js → schema + makeConcept + validators + projections
//   shared/<specialty>.js → concept records + recognitionPatterns
//   index.js → registry + ordered RECOGNITION_GROUPS + DOCUMENT_TYPE_HINTS (here)
//   evidenceConceptLibrary.js → facade (identical public API)
//
// PURE / Never-PHI. `makeConcept` is intentionally NOT re-exported (stays
// library-internal). Concept records are pure JSON data; recognition is the
// separate code-level table assembled here.
// ============================================================================

import {
  validateEvidenceConcept,
} from './conceptBuilder.js';

import { concepts as sharedConcepts, recognitionPatterns as sharedPatterns } from './shared.js';
import { concepts as hematologyConcepts, recognitionPatterns as hematologyPatterns } from './hematology.js';
import { concepts as neurologyConcepts, recognitionPatterns as neurologyPatterns } from './neurology.js';
import { concepts as cardiologyConcepts, recognitionPatterns as cardiologyPatterns } from './cardiology.js';
import { concepts as pulmonologyConcepts, recognitionPatterns as pulmonologyPatterns } from './pulmonology.js';
import { concepts as gastroenterologyConcepts, recognitionPatterns as gastroenterologyPatterns } from './gastroenterology.js';
import { concepts as hepatologyConcepts, recognitionPatterns as hepatologyPatterns } from './hepatology.js';
import { concepts as nephrologyConcepts, recognitionPatterns as nephrologyPatterns } from './nephrology.js';
import { concepts as endocrinologyConcepts, recognitionPatterns as endocrinologyPatterns } from './endocrinology.js';
import { concepts as rheumatologyConcepts, recognitionPatterns as rheumatologyPatterns } from './rheumatology.js';
import { concepts as orthopedicsConcepts, recognitionPatterns as orthopedicsPatterns } from './orthopedics.js';
import { concepts as dermatologyConcepts, recognitionPatterns as dermatologyPatterns } from './dermatology.js';
import { concepts as entConcepts, recognitionPatterns as entPatterns } from './ent.js';
import { concepts as ophthalmologyConcepts, recognitionPatterns as ophthalmologyPatterns } from './ophthalmology.js';
import { concepts as urologyConcepts, recognitionPatterns as urologyPatterns } from './urology.js';
import { concepts as obstetricsGynecologyConcepts, recognitionPatterns as obstetricsGynecologyPatterns } from './obstetrics_gynecology.js';
import { concepts as oncologyConcepts, recognitionPatterns as oncologyPatterns } from './oncology.js';
import { concepts as infectiousDiseaseConcepts, recognitionPatterns as infectiousDiseasePatterns } from './infectious_disease.js';
import { concepts as allergyImmunologyConcepts, recognitionPatterns as allergyImmunologyPatterns } from './allergy_immunology.js';
import { concepts as radiologyConcepts, recognitionPatterns as radiologyPatterns } from './radiology.js';
import { concepts as maternalFetalConcepts, recognitionPatterns as maternalFetalPatterns } from './maternal_fetal.js';
import { concepts as reproductiveEndocrinologyConcepts, recognitionPatterns as reproductiveEndocrinologyPatterns } from './reproductive_endocrinology.js';
import { concepts as vascularSurgeryConcepts, recognitionPatterns as vascularSurgeryPatterns } from './vascular_surgery.js';
import { concepts as generalSurgeryConcepts, recognitionPatterns as generalSurgeryPatterns } from './general_surgery.js';
import { concepts as neurosurgeryConcepts, recognitionPatterns as neurosurgeryPatterns } from './neurosurgery.js';
import { concepts as colorectalSurgeryConcepts, recognitionPatterns as colorectalSurgeryPatterns } from './colorectal_surgery.js';
import { concepts as thoracicSurgeryConcepts, recognitionPatterns as thoracicSurgeryPatterns } from './thoracic_surgery.js';
import { concepts as cardiothoracicSurgeryConcepts, recognitionPatterns as cardiothoracicSurgeryPatterns } from './cardiothoracic_surgery.js';
import { concepts as psychiatryConcepts, recognitionPatterns as psychiatryPatterns } from './psychiatry.js';
import { concepts as painManagementConcepts, recognitionPatterns as painManagementPatterns } from './pain_management.js';
import { concepts as physicalMedicineConcepts, recognitionPatterns as physicalMedicinePatterns } from './physical_medicine.js';
import { concepts as emergencyMedicineConcepts, recognitionPatterns as emergencyMedicinePatterns } from './emergency_medicine.js';
import { concepts as criticalCareConcepts, recognitionPatterns as criticalCarePatterns } from './critical_care.js';
import { concepts as anesthesiologyConcepts, recognitionPatterns as anesthesiologyPatterns } from './anesthesiology.js';
import { concepts as pathologyConcepts, recognitionPatterns as pathologyPatterns } from './pathology.js';
import { concepts as clinicalLabConcepts, recognitionPatterns as clinicalLabPatterns } from './clinical_lab.js';
import { concepts as palliativeCareConcepts, recognitionPatterns as palliativeCarePatterns } from './palliative_care.js';
import { concepts as pediatricsConcepts, recognitionPatterns as pediatricsPatterns } from './pediatrics.js';
import { concepts as internalMedicineConcepts, recognitionPatterns as internalMedicinePatterns } from './internal_medicine.js';
import { concepts as geriatricsConcepts, recognitionPatterns as geriatricsPatterns } from './geriatrics.js';
import { concepts as sleepMedicineConcepts, recognitionPatterns as sleepMedicinePatterns } from './sleep_medicine.js';
import { concepts as geneticsConcepts, recognitionPatterns as geneticsPatterns } from './genetics.js';
import { concepts as occupationalMedicineConcepts, recognitionPatterns as occupationalMedicinePatterns } from './occupational_medicine.js';
import { concepts as sportsMedicineConcepts, recognitionPatterns as sportsMedicinePatterns } from './sports_medicine.js';
import { concepts as addictionMedicineConcepts, recognitionPatterns as addictionMedicinePatterns } from './addiction_medicine.js';

// ── Re-export the conceptBuilder PUBLIC API (explicit — NOT `export *`, so
//    makeConcept and internal helpers never leak through the facade) ──────────
export {
  CONCEPT_SCHEMA_VERSION,
  CONCEPT_TYPES,
  TARGET_TYPES,
  CONCEPT_VALUE_TYPES,
  CONCEPT_EVIDENCE_MODES,
  GOVERNANCE_SOURCES,
  OWNER_SCOPES,
  CONCEPT_STATUS,
  CONCEPT_REVIEW_STATUS,
  DEFAULT_POLARITY_RULE,
  SHARED_SPECIALTY_ID,
  resolveTargetCompatibility,
  validateEvidenceConcept,
  conceptToFieldEvidenceMap,
  conceptToOptionEvidenceMap,
  conceptToRedFlagEvidenceMap,
  conceptToAttachmentEvidenceMap,
} from './conceptBuilder.js';

// ── Frozen registry + fast index ─────────────────────────────────────────────
// Aggregation order (shared → hematology → neurology) is presentation-only;
// resolution (getConceptsForSpecialty) is order-independent by construction.
const _CONCEPTS = [
  ...sharedConcepts,
  ...hematologyConcepts,
  ...neurologyConcepts,
  ...cardiologyConcepts,
  ...pulmonologyConcepts,
  ...gastroenterologyConcepts,
  ...hepatologyConcepts,
  ...nephrologyConcepts,
  ...endocrinologyConcepts,
  ...rheumatologyConcepts,
  ...orthopedicsConcepts,
  ...dermatologyConcepts,
  ...entConcepts,
  ...ophthalmologyConcepts,
  ...urologyConcepts,
  ...obstetricsGynecologyConcepts,
  ...oncologyConcepts,
  ...infectiousDiseaseConcepts,
  ...allergyImmunologyConcepts,
  ...radiologyConcepts,
  ...maternalFetalConcepts,
  ...reproductiveEndocrinologyConcepts,
  ...vascularSurgeryConcepts,
  ...generalSurgeryConcepts,
  ...neurosurgeryConcepts,
  ...colorectalSurgeryConcepts,
  ...thoracicSurgeryConcepts,
  ...cardiothoracicSurgeryConcepts,
  ...psychiatryConcepts,
  ...painManagementConcepts,
  ...physicalMedicineConcepts,
  ...emergencyMedicineConcepts,
  ...criticalCareConcepts,
  ...anesthesiologyConcepts,
  ...pathologyConcepts,
  ...clinicalLabConcepts,
  ...palliativeCareConcepts,
  ...pediatricsConcepts,
  ...internalMedicineConcepts,
  ...geriatricsConcepts,
  ...sleepMedicineConcepts,
  ...geneticsConcepts,
  ...occupationalMedicineConcepts,
  ...sportsMedicineConcepts,
  ...addictionMedicineConcepts,
];

export const EVIDENCE_CONCEPTS = Object.freeze(_CONCEPTS);
const CONCEPT_INDEX = new Map(_CONCEPTS.map((c) => [c.conceptId, c]));

const isSharedSpecialty = (s) => s == null || s === 'shared';

// ============================================================================
// PUBLIC READ HELPERS (pure)
// ============================================================================

/** @returns {Array} every concept in the library (frozen). */
export const getAllEvidenceConcepts = () => EVIDENCE_CONCEPTS;

/** @returns {Object|null} the concept for a conceptId, or null. */
export const getConcept = (conceptId) =>
  (typeof conceptId === 'string' && CONCEPT_INDEX.get(conceptId)) || null;

/** @returns {Array} shared/global concepts (specialtyId == null). */
export const getSharedEvidenceConcepts = () =>
  EVIDENCE_CONCEPTS.filter((c) => isSharedSpecialty(c.specialtyId));

/**
 * Concepts usable within a specialty: exact specialty matches FIRST, then
 * shared/global concepts. When called with null/'shared', returns only shared.
 * @param {string|null} specialtyId
 * @returns {Array}
 */
export const getConceptsForSpecialty = (specialtyId) => {
  if (isSharedSpecialty(specialtyId)) return getSharedEvidenceConcepts();
  const exact = EVIDENCE_CONCEPTS.filter((c) => c.specialtyId === specialtyId);
  const shared = getSharedEvidenceConcepts();
  return [...exact, ...shared];
};

/** @returns {Array} concepts of a given conceptType. */
export const getConceptsByType = (conceptType) =>
  EVIDENCE_CONCEPTS.filter((c) => c.conceptType === conceptType);

// ============================================================================
// LIBRARY VALIDATOR (per-concept validation + duplicate-conceptId detection)
// ============================================================================

const isStr = (v) => typeof v === 'string' && v.trim().length > 0;
const isArr = (v) => Array.isArray(v);

/**
 * Validate the whole library (or any concept array): per-concept validation +
 * duplicate-conceptId detection.
 * @param {Array} concepts
 * @returns {{ valid: boolean, errors: string[], total: number, byType: Object }}
 */
export const validateEvidenceConceptLibrary = (concepts = EVIDENCE_CONCEPTS) => {
  const errors = [];
  if (!isArr(concepts)) {
    return { valid: false, errors: ['Concept library must be an array.'], total: 0, byType: {} };
  }

  const seen = new Set();
  const byType = {};
  for (const concept of concepts) {
    errors.push(...validateEvidenceConcept(concept));
    const cid = concept?.conceptId;
    if (isStr(cid)) {
      if (seen.has(cid)) errors.push(`[${cid}] duplicate conceptId.`);
      seen.add(cid);
    }
    const ct = concept?.conceptType;
    if (isStr(ct)) byType[ct] = (byType[ct] || 0) + 1;
  }

  return { valid: errors.length === 0, errors, total: concepts.length, byType };
};

// ============================================================================
// RECOGNITION — code-level deterministic tables (Never-PHI). Concept records
// above stay pure JSON (no RegExp); recognition lives here, assembled from the
// data modules' recognitionPatterns + the ORDERED group membership.
//
// The patterns are merged (order-independent; keyed by conceptId). The GROUP
// membership is owned here as explicit ordered arrays because precedence
// (first-match-wins in findConceptByText) is a cross-module global concern. The
// arrays reproduce the pre-split order EXACTLY; the L-1 LIVE findings are
// appended to FINDING. DATA_ONLY concepts are intentionally absent from every
// group → never recognition-reachable.
// ============================================================================

const CONCEPT_RECOGNITION_PATTERNS = Object.freeze({
  ...sharedPatterns,
  ...hematologyPatterns,
  ...neurologyPatterns,
  ...cardiologyPatterns,
  ...pulmonologyPatterns,
  ...gastroenterologyPatterns,
  ...hepatologyPatterns,
  ...nephrologyPatterns,
  ...endocrinologyPatterns,
  ...rheumatologyPatterns,
  ...orthopedicsPatterns,
  ...dermatologyPatterns,
  ...entPatterns,
  ...ophthalmologyPatterns,
  ...urologyPatterns,
  ...obstetricsGynecologyPatterns,
  ...oncologyPatterns,
  ...infectiousDiseasePatterns,
  ...allergyImmunologyPatterns,
  ...radiologyPatterns,
  ...maternalFetalPatterns,
  ...reproductiveEndocrinologyPatterns,
  ...vascularSurgeryPatterns,
  ...generalSurgeryPatterns,
  ...neurosurgeryPatterns,
  ...colorectalSurgeryPatterns,
  ...thoracicSurgeryPatterns,
  ...cardiothoracicSurgeryPatterns,
  ...psychiatryPatterns,
  ...painManagementPatterns,
  ...physicalMedicinePatterns,
  ...emergencyMedicinePatterns,
  ...criticalCarePatterns,
  ...anesthesiologyPatterns,
  ...pathologyPatterns,
  ...clinicalLabPatterns,
  ...palliativeCarePatterns,
  ...pediatricsPatterns,
  ...internalMedicinePatterns,
  ...geriatricsPatterns,
  ...sleepMedicinePatterns,
  ...geneticsPatterns,
  ...occupationalMedicinePatterns,
  ...sportsMedicinePatterns,
  ...addictionMedicinePatterns,
});

export const RECOGNITION_GROUPS = Object.freeze({
  // LAB: existing CBC measures + shared creatinine/CBC, then L-3 hematology
  //      measurements + lab documents, then L-4 cardiology measurements +
  //      documents (mutually-exclusive patterns → order free).
  LAB:      Object.freeze([
    // L-7 nephrology urine-ratio concepts — FIRST. Their patterns contain
    // "albumin/protein:creatinine ratio" + "urine albumin", so they MUST be
    // checked before shared `creatinine` (/creatinine/) AND hepatology `albumin`
    // (/albumin(?!uria)/) for first-match-wins to bind the urine ratio, not the
    // serum value. (Same technique as fev1_fvc_ratio before fev1 in L-5.)
    'urine_acr', 'urine_pcr',
    'white_blood_cell_count', 'platelet_count', 'hemoglobin', 'creatinine', 'complete_blood_count',
    // L-8 shared-labs promotion (universal cross-specialty labs; order free — distinct patterns)
    'hba1c', 'tsh',
    // L-10 shared-labs promotion (CRP/ESR; universal acute-phase markers; order free)
    'c_reactive_protein', 'erythrocyte_sedimentation_rate',
    // L-3 hematology measurements
    'ferritin', 'mean_corpuscular_volume', 'vitamin_b12', 'folate', 'reticulocyte_count',
    'lactate_dehydrogenase', 'haptoglobin', 'absolute_neutrophil_count', 'lymphocyte_count',
    // L-3 hematology documents (LAB_RESULT; CBC precedent)
    'blood_film', 'iron_studies', 'coagulation_studies',
    // L-4 cardiology measurements
    'troponin', 'bnp', 'nt_probnp', 'ejection_fraction', 'qtc_interval', 'ldl_cholesterol',
    // L-4 cardiology documents (alias binding; canonical doc types)
    'ecg_report', 'echocardiogram_report', 'lipid_panel',
    // L-5 pulmonology measurements — fev1_fvc_ratio BEFORE fev1/fvc (else /\bfev1\b/
    // and /\bfvc\b/ shadow the ratio concept for "FEV1/FVC ratio").
    'oxygen_saturation', 'fev1_fvc_ratio', 'fev1', 'fvc', 'peak_flow', 'dlco', 'pco2', 'po2',
    // L-5 pulmonology documents (alias binding; canonical doc types)
    'chest_xray_report', 'chest_ct_report', 'pulmonary_function_test', 'spirometry_report', 'arterial_blood_gas_result',
    // L-6 gastroenterology measurements + documents
    'fecal_calprotectin', 'fit_test', 'amylase', 'lipase',
    // L-13: nasal_endoscopy_report BEFORE GI endoscopy_report — GI's /endoscopy/i
    // would otherwise shadow "nasal endoscopy". (nasal_endoscopy only grabs "nasal
    // endoscopy"/"nasendoscopy"; plain "endoscopy report" still falls through to GI.)
    'nasal_endoscopy_report',
    'endoscopy_report', 'gastroscopy_report', 'colonoscopy_report', 'abdominal_ultrasound_report', 'abdominal_ct_report',
    // L-6 hepatology measurements + documents. `liver_function_test_result` uses the
    // full phrase only (NOT bare /lfts/) so it does not shadow abnormal_liver_function_tests.
    'alanine_aminotransferase', 'aspartate_aminotransferase', 'alkaline_phosphatase', 'gamma_glutamyl_transferase', 'albumin',
    'liver_function_test_result', 'fibroscan_report', 'hepatitis_serology_result',
    // L-7 nephrology remaining LAB members (mutually distinct → appended; eGFR/urea
    // collide with nothing earlier; renal documents bind via alias).
    'estimated_glomerular_filtration_rate', 'urea',
    'renal_ultrasound_report', 'urinalysis_result', 'urine_microscopy_result',
    // L-8 endocrinology measurements + documents (mutually distinct → appended)
    'glucose', 'free_t4', 'free_t3', 'cortisol', 'acth', 'prolactin',
    'thyroid_function_test_result', 'thyroid_ultrasound_report', 'pituitary_mri_report', 'adrenal_ct_report', 'dexa_scan_report',
    // L-10 rheumatology serologies + documents (mutually distinct → appended).
    'antinuclear_antibody', 'rheumatoid_factor', 'anti_ccp', 'hla_b27', 'uric_acid', 'creatine_kinase',
    'joint_aspiration_result', 'synovial_fluid_analysis',
    // L-11 orthopedics document (spine MRI; mutually distinct → appended).
    'spine_mri_report',
    // L-12 dermatology document (skin biopsy; mutually distinct → appended).
    'skin_biopsy_report',
    // L-13 ENT documents (audiology + sinus/laryngoscopy; mutually distinct → appended).
    // `nasal_endoscopy_report` is front-ordered before GI endoscopy_report (above).
    'audiogram_report', 'tympanometry_result', 'sinus_ct_report', 'laryngoscopy_report',
    // L-14 ophthalmology documents (OCT / visual field / fundus / retinal imaging;
    // mutually distinct → appended). Eye-specific phrases; no generic imaging_report/
    // lab_result concept sits in the LAB group to shadow them. (DATA_ONLY eye docs —
    // diabetic_eye_screening_report / slit_lamp_exam / visual_acuity_result /
    // intraocular_pressure_measurement — are pattern-less and NOT here.)
    'oct_report', 'visual_field_test', 'fundus_photography_report', 'retinal_imaging_report',
    // L-15 urology documents (ct_kub MOVED from nephrology + new urologic docs; mutually
    // distinct → appended). Eye/renal/urine docs above don't shadow them; psa_result
    // requires "result/test/level" (so raised_psa FINDING owns "raised PSA");
    // urine_culture distinct from nephrology urinalysis/urine-microscopy.
    'ct_kub_report', 'ct_urogram_report', 'prostate_mri_report', 'scrotal_ultrasound_report',
    'psa_result', 'cystoscopy_report', 'urine_culture_result',
    // L-16 obstetrics_gynecology documents (pelvic/TV US + cervical screening / colposcopy /
    // HPV / endometrial biopsy / hysteroscopy; mutually distinct → appended). cervical_
    // screening_result requires "result/report/Pap" so it does not shadow the abnormal_
    // cervical_screening FINDING. endometrial_biopsy_report safe (shared pathology_report
    // is DATA_ONLY/inert). colposcopy/hysteroscopy distinct from GI colonoscopy/endoscopy +
    // urology cystoscopy.
    'pelvic_ultrasound_report', 'transvaginal_ultrasound_report', 'colposcopy_report',
    'cervical_screening_result', 'hpv_test_result', 'endometrial_biopsy_report', 'hysteroscopy_report',
    // L-17 oncology documents (treatment plans + tumor board + staging / PET-CT; mutually
    // distinct → appended). staging_scan_report ("staging CT/MRI/PET") + pet_ct_report
    // ("PET-CT") don't shadow organ CTs (require staging/pet); tumor_board distinct from any
    // scope doc; chemotherapy_plan requires "plan" (hematology chemotherapy_exposure inert).
    // L-19: allergen_immunotherapy_plan FRONT-ORDERED before oncology immunotherapy_plan —
    // GI's... no: oncology's /immunotherapy plan/ would otherwise shadow "allergen
    // immunotherapy plan". (allergen_immunotherapy_plan only grabs "allergen immunotherapy"/
    // "desensitization"; plain "immunotherapy plan" still falls through to oncology.)
    'allergen_immunotherapy_plan',
    'oncology_treatment_plan', 'chemotherapy_plan', 'radiotherapy_plan', 'immunotherapy_plan',
    'tumor_board_report', 'staging_scan_report', 'pet_ct_report',
    // L-18 infectious_disease documents (microbiology/culture/susceptibility + OPAT;
    // mutually distinct → appended). blood_culture_result ("blood culture") distinct from
    // urology urine_culture_result; microbiology/susceptibility distinct; opat_plan requires
    // plan/referral/therapy (correction 4 — bare OPAT never).
    'blood_culture_result', 'microbiology_culture_result', 'antibiotic_susceptibility_result', 'opat_plan',
    // L-19 allergy_immunology documents (IgE / Ig panel / tryptase / complement / skin-prick /
    // C1-INH; allergen_immunotherapy_plan is front-ordered above). c1_esterase_inhibitor_result
    // requires result/test/level/report ("C1 esterase inhibitor deficiency" → hereditary_
    // angioedema finding, not here); complement_test_result distinct from rheum complement_c3_c4
    // (inert). Mutually distinct → appended.
    'skin_prick_test_result', 'specific_ige_result', 'immunoglobulin_panel', 'serum_tryptase_result',
    'complement_test_result', 'c1_esterase_inhibitor_result',
    // L-20 radiology documents — appended LAST (frame/document specialty; only 3 LIVE docs,
    // all phrase-unique image-guided/MRI-safety surfaces no organ + no shared concept owns).
    // EXACT-LABEL / context patterns (corrections 1 & 2): "no CT-guided biopsy" / "percutaneous
    // drainage not performed" / "MRI safety policy" → 0. Generic ct/mri/ultrasound/xray reports
    // are DATA_ONLY (reuse shared imaging_report; organ-specific reports own recognition).
    'image_guided_biopsy_report', 'image_guided_drainage_report', 'mri_safety_screening_form',
    // L-21 maternal_fetal documents — appended LAST. Pregnancy-specific antenatal scans;
    // OB-GYN pelvic/transvaginal ultrasound + radiology generic ultrasound untouched (distinct
    // phrases). Correction 4: CTG/growth-scan/nuchal-translucency/cervical-length require
    // exact-label or report/result context ("CTG not required"/"prior CTG" → 0). nuchal never
    // bare (neurology "nuchal rigidity"); cervical_length full phrase (OB-GYN reserves cervical).
    'obstetric_ultrasound_report', 'dating_scan_report', 'fetal_anomaly_scan_report', 'fetal_growth_scan_report',
    'nuchal_translucency_result', 'cervical_length_scan_report', 'fetal_doppler_report', 'cardiotocography_result',
    // L-22 reproductive_endocrinology documents — appended LAST. Fertility-workup labs + tubal/
    // cavity imaging + assisted-reproduction plans. Correction 3/4: result/report/plan context
    // required (bare AMH/HSG/AFC/IVF/IUI + not-done/history → 0). FSH/LH/estradiol/prolactin stay
    // endocrinology; OB-GYN hysteroscopy/pelvic/TV ultrasound + radiology generic ultrasound untouched.
    'amh_result', 'fertility_hormone_profile', 'semen_analysis_result', 'antral_follicle_count_report',
    'hysterosalpingogram_report', 'hycosy_report', 'sonohysterogram_report',
    'ivf_treatment_plan', 'iui_treatment_plan', 'embryo_transfer_plan', 'ovulation_induction_plan', 'fertility_preservation_plan',
    // L-23 vascular_surgery documents — appended LAST. Vascular-specific duplex/ABI/angiogram/
    // procedure docs. Correction 2: report/result/scan/procedure context required (bare ABI/ABPI/
    // TBI/EVAR/CEA + prior/not-required/not-planned → 0). Distinct from cardiology coronary CTA/
    // CABG + radiology generic angiography_report (all reused inert).
    'arterial_duplex_report', 'venous_duplex_report', 'carotid_duplex_report', 'aortic_ultrasound_report',
    'venous_reflux_study_report', 'peripheral_angiogram_report', 'ct_angiogram_runoff_report',
    'ankle_brachial_index_result', 'toe_brachial_index_result',
    'evar_report', 'carotid_endarterectomy_report', 'vascular_bypass_graft_report',
    // L-24 general_surgery document — appended LAST. Soft-tissue ultrasound for the lump
    // pathway; distinct from GI abdominal_ultrasound (/abdominal ultrasound/) + radiology
    // generic ultrasound (inert). Bare "ultrasound" → 0 (requires "soft tissue").
    'soft_tissue_ultrasound_report',
    // L-25 neurosurgery documents — appended LAST. Brain/head MRI + CT head reports;
    // distinct from endo pituitary_mri_report (/pituitary mri/), ortho spine_mri_report
    // (/spine mri/), GI abdominal_ct (/ct abdomen/). Bare CT/MRI handled by DOCUMENT_TYPE_HINTS.
    'brain_mri_report', 'ct_head_report',
    // L-26 colorectal_surgery documents — appended LAST. Anorectal physiology + rectal-
    // staging MRI; distinct from GI colonoscopy/abdominal CT/US + radiology generic imaging.
    // rectal_mri (/rectal mri/, /mri rectum/) ≠ neuro brain_mri / endo pituitary_mri / ortho spine_mri.
    'anorectal_manometry_report', 'defecating_proctogram_report', 'rectal_mri_report',
    // L-27 thoracic_surgery document — appended LAST. Mediastinal staging procedure;
    // distinct from pulmonology chest CT/CXR + cardiology cardiac imaging. Shared consult_
    // note / operative_report reused (no thoracic consult/operative doc created).
    'mediastinoscopy_report',
    // L-34A pathology documents — appended LAST. Specific specimen-report surfaces; shared
    // pathology_report is generic/inert (not duplicated). Organ biopsy reports (derm skin_biopsy_
    // report, hep liver_biopsy_report, neph renal_biopsy_report, OB-GYN endometrial_biopsy_report,
    // radiology image_guided_biopsy_report) + urology urine_cytology_result are EARLIER in LAB and
    // keep their organ-qualified phrases; generic "histology/cytology/surgical pathology/IHC/
    // molecular pathology report" falls through to these.
    'histology_report', 'cytology_report', 'surgical_pathology_report',
    'immunohistochemistry_report', 'molecular_pathology_report',
  ]),
  // FINDING: hematology findings — anemia SUBTYPES ordered BEFORE generic `anemia`
  //          (else /an[ae]mia/ shadows them); `leukopenia` tightened so the
  //          dedicated `neutropenia` binds. Then L-1 shared + L-2 neurology +
  //          L-4 cardiology (mutually distinct patterns; appended).
  FINDING:  Object.freeze([
    'leukopenia', 'leukocytosis', 'thrombocytopenia',
    'iron_deficiency_anemia', 'macrocytic_anemia', 'anemia',   // subtypes BEFORE generic anemia
    'neutropenia', 'thrombocytosis', 'pancytopenia', 'polycythemia', 'lymphocytosis', 'lymphadenopathy', 'splenomegaly',
    'abnormal_lab', 'abnormal_imaging', 'prior_workup',
    'headache', 'seizure', 'neuropathy', 'movement_disorder', 'cognitive_decline', 'demyelinating_disease',
    'atrial_fibrillation', 'heart_failure', 'valvular_heart_disease', 'coronary_artery_disease', 'cardiomyopathy',
    'suspected_angina', 'exertional_chest_pain', 'palpitations', 'murmur', 'arrhythmia',
    // L-5 pulmonology findings (mutually distinct; appended)
    'asthma', 'copd', 'chronic_cough', 'wheeze', 'hemoptysis', 'bronchiectasis',
    'interstitial_lung_disease', 'pulmonary_fibrosis', 'pulmonary_nodule', 'pleural_effusion',
    'recurrent_chest_infections', 'pleuritic_chest_pain',
    // L-6 gastroenterology findings
    'dysphagia', 'rectal_bleeding', 'chronic_diarrhea', 'inflammatory_bowel_disease', 'crohns_disease',
    'ulcerative_colitis', 'coeliac_disease', 'reflux_symptoms', 'gallstones', 'pancreatitis',
    // L-6 hepatology findings
    'jaundice', 'cirrhosis', 'fatty_liver', 'hepatitis', 'abnormal_liver_function_tests',
    // L-7 nephrology findings (mutually distinct → appended). `albuminuria` is LIVE
    // here (Option A) and wins for fields only because hepatology `albumin` is
    // narrowed to /albumin(?!uria)/ (FINDING is searched after LAB for fields).
    'chronic_kidney_disease', 'acute_kidney_injury', 'reduced_renal_function',
    'proteinuria', 'albuminuria', 'nephrotic_syndrome', 'nephritic_syndrome',
    'polycystic_kidney_disease', 'hematuria',
    // L-8 endocrinology findings. Diabetes subtypes FIRST (their patterns cover prefix
    // + suffix wording: "type 2 diabetes"/"diabetes mellitus type 2"/"diabetes type 2"/
    // T2DM/T2D), so they win over generic `diabetes_mellitus` (which excludes
    // "diabetes insipidus" via /\bdiabetes\b(?! insipidus)/).
    'type_1_diabetes', 'type_2_diabetes', 'diabetes_mellitus',
    'hyperglycemia', 'hypoglycemia',
    'hypothyroidism', 'hyperthyroidism', 'thyrotoxicosis', 'graves_disease', 'hashimoto_thyroiditis', 'thyroid_nodule', 'goitre',
    'adrenal_insufficiency', 'cushing_syndrome', 'addison_disease', 'adrenal_incidentaloma', 'pheochromocytoma',
    'hyperprolactinemia', 'pituitary_adenoma',
    'hyperparathyroidism', 'hypercalcemia', 'hypocalcemia', 'osteoporosis',
    // L-10 rheumatology findings. Specific inflammatory subtypes FIRST; generic
    // `inflammatory_arthritis` after them (shared `arthritis` is DATA_ONLY/pattern-less
    // → never competes). giant_cell_arteritis before generic vasculitis.
    'rheumatoid_arthritis', 'psoriatic_arthritis', 'ankylosing_spondylitis', 'axial_spondyloarthritis', 'inflammatory_arthritis',
    'giant_cell_arteritis', 'vasculitis', 'polymyalgia_rheumatica', 'systemic_lupus_erythematosus', 'gout',
    'sjogren_syndrome', 'systemic_sclerosis', 'dermatomyositis', 'polymyositis', 'raynaud_phenomenon',
    'synovitis', 'enthesitis',
    // L-11 orthopedics findings (structural/mechanical/degenerative). osteoarthritis is
    // distinct from rheumatology inflammatory subtypes (appended above) and from the
    // shared generic `arthritis` (DATA_ONLY/pattern-less). Generic fracture/dislocation
    // are DATA_ONLY (no-negation safety) → NOT here. fracture_dislocation tightly patterned.
    'osteoarthritis', 'fracture_dislocation', 'stress_fracture',
    'rotator_cuff_tear', 'meniscal_tear', 'ligament_injury', 'tendon_rupture',
    'spinal_stenosis', 'disc_herniation', 'radiculopathy', 'sciatica', 'scoliosis',
    // L-12 dermatology findings (skin disease + lesion/cancer + skin infection).
    // psoriasis (skin) is distinct from rheumatology psoriatic_arthritis; generic
    // rash/mole/lesion/wound/ulcer are DATA_ONLY (pattern-less). BCC/SCC skin-specific.
    'eczema', 'psoriasis', 'seborrheic_dermatitis', 'contact_dermatitis', 'urticaria',
    'acne', 'rosacea', 'alopecia', 'hidradenitis_suppurativa', 'actinic_keratosis',
    'suspicious_pigmented_lesion', 'basal_cell_carcinoma', 'squamous_cell_carcinoma',
    // L-24 general_surgery FRONT-ORDER: pilonidal_disease + intra_abdominal_abscess MUST
    // precede dermatology's broad `abscess` (/abscess/i) so "pilonidal abscess" + "intra-
    // abdominal abscess" resolve to general_surgery; "skin"/"cutaneous abscess" still → derm.
    // (cellulitis's /cellulitis/ does not collide, so ordering before it is harmless.)
    'pilonidal_disease', 'intra_abdominal_abscess',
    // L-26 colorectal_surgery FRONT-ORDER: perianal_abscess (/perianal|perirectal|ischiorectal
    // abscess/) + complicated_diverticulitis (/diverticular abscess/) MUST precede dermatology's
    // broad `abscess` (/abscess/i) so they resolve to colorectal_surgery; "skin"/"cutaneous
    // abscess" still → dermatology. (Same first-match-wins technique as L-24 pilonidal/intra-abdo.)
    'perianal_abscess', 'complicated_diverticulitis',
    'cellulitis', 'abscess', 'impetigo',
    // L-13 ENT findings (ear / nose / throat / voice / salivary / vestibular). Generic
    // ear/nose/throat symptoms are DATA_ONLY (pattern-less); recurrent_tonsillitis is
    // the LIVE one (tonsillitis DATA_ONLY); vertigo stays neurology (bppv/meniere ENT).
    'hearing_loss', 'tinnitus', 'otitis_externa', 'otitis_media', 'tympanic_membrane_perforation', 'cholesteatoma',
    'chronic_sinusitis', 'nasal_polyps', 'deviated_septum',
    'recurrent_tonsillitis', 'dysphonia', 'vocal_cord_lesion',
    'parotid_swelling', 'salivary_gland_stone', 'bppv', 'meniere_disease',
    // L-14 ophthalmology findings (eye disease / retina / glaucoma / anterior eye).
    // CORRECTION 1: dry_eye BEFORE conjunctivitis (dry_eye owns /keratoconjunctivitis
    // sicca/; conjunctivitis is word-bounded /\bconjunctivitis\b/ and never matches
    // inside "keratoconjunctivitis"). diabetic_retinopathy MOVED here from endocrinology
    // (now LIVE). glaucoma is broad; acute angle closure is a RED_FLAG (other group).
    'cataract', 'glaucoma', 'raised_intraocular_pressure', 'visual_field_defect',
    'macular_degeneration', 'diabetic_retinopathy', 'retinal_vein_occlusion',
    'dry_eye', 'conjunctivitis', 'blepharitis', 'keratitis', 'uveitis',
    // L-15 urology findings (stones-obstruction MOVED from nephrology + prostate / voiding /
    // scrotal). CORRECTIONS 1 & 2: hydronephrosis / nephrolithiasis / ureteric_stone use
    // POSITIVE-CONTEXT patterns (rule-out language never fires — findings have no
    // match-time negation). nephrology `hematuria` stays LIVE (urology visible/microscopic
    // hematuria are DATA_ONLY/inert). raised_psa never shadowed by psa_result (LAB requires
    // "result/test/level"). BPH/LUTS word-bounded.
    'hydronephrosis', 'nephrolithiasis', 'ureteric_stone', 'renal_colic',
    'urinary_retention', 'lower_urinary_tract_symptoms', 'benign_prostatic_hyperplasia',
    'raised_psa', 'prostatitis', 'hydrocele', 'varicocele', 'epididymitis', 'testicular_lump',
    // L-16 obstetrics_gynecology findings (menstrual bleeding / pelvic benign disease /
    // cervical screening / gyne infection). Specific phrases — generic bleeding/pain/mass/
    // cyst stay DATA_ONLY. abnormal_cervical_screening owns "abnormal cervical screening"
    // (cervical_screening_result doc requires "result/report"). pelvic_organ_prolapse never
    // bare "prolapse". high_risk_hpv (FINDING "HPV positive") vs hpv_test_result (LAB).
    'heavy_menstrual_bleeding', 'intermenstrual_bleeding', 'postcoital_bleeding',
    'endometriosis', 'uterine_fibroids', 'ovarian_cyst', 'pelvic_organ_prolapse',
    'abnormal_cervical_screening', 'high_risk_hpv', 'cervical_dysplasia',
    'pelvic_inflammatory_disease', 'bartholin_cyst',
    // L-17 oncology findings (central cancer-care, POSITIVE-CONTEXT only — never bare
    // /metastatic/ or /recurrence/; "no/known/ruled out" do not fire). Organ cancer
    // suspicion + diagnoses are NOT here (organ-owned). Correction 1: no "known metastatic".
    'metastatic_disease', 'cancer_recurrence',
    // L-18 infectious_disease findings (complex/systemic/high-consequence — organ-local
    // infections are NOT here). bacteremia ANCHORED/positive-context (correction 1);
    // active_tuberculosis/hiv_infection/antimicrobial_resistant_infection require active/
    // infection context (never bare TB/HIV/MRSA; colonization → 0). FUO/PUO word-bounded.
    'fever_of_unknown_origin', 'bacteremia', 'active_tuberculosis', 'hiv_infection', 'antimicrobial_resistant_infection',
    // L-19 allergy_immunology findings (hypersensitivity + immunodeficiency diagnoses).
    // Corrections 1 & 2: allergy + immunodeficiency findings are EXACT-LABEL / positive-
    // context (NKDA / rule-out safe — "no known drug allergies"/"CVID ruled out" → 0).
    // Correction 3: hereditary_angioedema (incl. anchored "C1 esterase inhibitor deficiency")
    // distinct from c1_esterase_inhibitor_result document. Organ skin/lung allergy stays
    // dermatology/pulmonology.
    'food_allergy', 'drug_allergy', 'penicillin_allergy', 'venom_allergy', 'hereditary_angioedema',
    'primary_immunodeficiency', 'common_variable_immunodeficiency', 'hypogammaglobulinemia',
    // L-21 maternal_fetal findings (pregnancy-risk; mutually distinct → appended). Corrections
    // 1 & 2: placenta_previa/placenta_accreta/fetal_growth_restriction/gestational_hypertension
    // use exact-label/positive-context (no-negation safe). multiple/monochorionic/alloimmunization
    // phrase-specific. OB-GYN/endo/cardio/nephro/heme general concepts untouched.
    'high_risk_pregnancy', 'fetal_growth_restriction', 'gestational_hypertension',
    'placenta_previa', 'placenta_accreta', 'multiple_pregnancy', 'monochorionic_twins', 'red_cell_alloimmunization',
    // L-22 reproductive_endocrinology findings (fertility pathway; mutually distinct → appended).
    // pcos/amenorrhea/infertility/premature_ovarian_insufficiency MOVED from endocrinology (LIVE).
    // Corrections 1 & 2: exact-label/positive-context (no/ruled-out/resolved/bare-POI → 0).
    'infertility', 'pcos', 'premature_ovarian_insufficiency', 'amenorrhea',
    'anovulation', 'ovulation_disorder', 'diminished_ovarian_reserve', 'male_factor_infertility',
    // L-23 vascular_surgery findings (vessel-mechanism; mutually distinct → appended). Correction
    // 3: exact-label/positive-context (no/ruled-out/screening-normal/history → 0). Never bare
    // /claudication/ (rheum "jaw claudication"); carotid_stenosis carries NO TIA/stroke (corr 5).
    'peripheral_arterial_disease', 'intermittent_claudication', 'carotid_stenosis',
    'abdominal_aortic_aneurysm', 'varicose_veins', 'chronic_venous_insufficiency',
    // L-24 general_surgery findings (mutually distinct → appended last; pilonidal_disease +
    // intra_abdominal_abscess are FRONT-ORDERED above, before dermatology abscess). Correction
    // 2: exact-label/positive-context (no-negation safe); never bare /hernia/ (orthopedics
    // disc_herniation) or /cyst/. GI gallstones/pancreatitis + hepatology cholangitis untouched.
    'appendicitis', 'acute_cholecystitis', 'biliary_colic', 'groin_hernia', 'ventral_hernia',
    'lipoma', 'sebaceous_cyst', 'surgical_site_infection',
    // L-25 neurosurgery findings (mutually distinct → appended last; NO front-ordering).
    // Qualified phrases only — never bare brain/mass/tumour/lesion/myelopathy/disc. endo
    // pituitary_adenoma keeps "pituitary adenoma"; GS surgical_site_infection keeps generic
    // SSI; ortho cervical_stenosis ≠ "cervical myelopathy"; ID epidural ABSCESS ≠ epidural HEMATOMA.
    'intracranial_mass', 'brain_tumor', 'pituitary_mass', 'hydrocephalus', 'chiari_malformation',
    'subdural_hematoma', 'extradural_hematoma', 'cervical_myelopathy', 'post_craniotomy_infection',
    // L-26 colorectal_surgery findings (mutually distinct → appended last; perianal_abscess +
    // complicated_diverticulitis are FRONT-ORDERED above, before dermatology abscess). Scoped
    // phrases — GI rectal_bleeding/IBD + OB-GYN rectocele + general_surgery stoma_complication untouched.
    'anal_fissure', 'anal_fistula', 'hemorrhoids', 'rectal_prolapse', 'fecal_incontinence',
    'stoma_prolapse', 'stoma_retraction',
    // L-27 thoracic_surgery findings (mutually distinct → appended last; NO front-ordering).
    // Scoped surgical phrases — pulmonology pulmonary_nodule/pleural_effusion/pneumothorax_
    // suspected + cardiology + oncology lung_cancer (reserved) + vascular thoracic_aortic_
    // aneurysm + general_surgery hiatus_hernia/gallbladder_empyema all untouched.
    'recurrent_pneumothorax', 'persistent_air_leak', 'pleural_empyema', 'trapped_lung',
    'mediastinal_mass', 'thymoma', 'pectus_excavatum', 'chest_wall_deformity',
    'lung_cancer_surgical_resection', 'surgical_lung_biopsy',
    // L-27B cardiothoracic_surgery findings (mutually distinct → appended last; NO front-ordering).
    // Explicit SURGICAL phrases only — cardiology valvular_heart_disease/coronary_artery_disease/
    // heart_failure/prior_cabg + vascular thoracic_aortic_aneurysm/aortic_aneurysm + general_surgery
    // surgical_site_infection (incl. /wound dehiscence/) + thoracic pleural/mediastinal all untouched.
    // coronary_bypass_surgery parks bare/prior CABG; valve concepts surgical-only (no TAVI/MitraClip);
    // ascending/root aortic aneurysm distinct from vascular AAA; post_sternotomy_infection sternotomy-
    // specific (sternal_wound_dehiscence stays DATA_ONLY → general_surgery owns "wound dehiscence").
    'coronary_bypass_surgery', 'valve_surgery_review', 'surgical_aortic_valve_replacement',
    'mitral_valve_surgery', 'ascending_aortic_aneurysm', 'aortic_root_aneurysm', 'post_sternotomy_infection',
    // L-28 psychiatry findings (mutually distinct → appended last; NO front-ordering).
    // Adult psychiatric referral surfaces only — neurology cognitive_decline(/dementia//memory)/
    // seizure/movement_disorder + pulmonology sleep_apnea + hematology alcohol_exposure +
    // orthopedics trauma_history + neurosurgery traumatic_brain_injury + endocrinology thyroid all
    // untouched. Named-disorder phrases only: never bare mood/anxiety/depression/stress/sleep/
    // trauma/ADHD/personality. first_episode_psychosis BEFORE psychosis (specific before generic;
    // both psychiatry-owned). 0 documents, 0 red flags; crisis/safety/catatonia stay DATA_ONLY.
    'first_episode_psychosis', 'psychosis', 'auditory_hallucinations', 'delusional_beliefs',
    'bipolar_disorder', 'manic_episode', 'treatment_resistant_depression', 'severe_depression_psychiatric_review',
    'panic_disorder', 'generalized_anxiety_disorder', 'obsessive_compulsive_disorder', 'post_traumatic_stress_disorder',
    'eating_disorder_assessment', 'adult_adhd_assessment', 'personality_disorder_assessment', 'psychiatric_medication_review',
    // L-29 pain_management findings (mutually distinct → appended last; NO front-ordering).
    // Pain-PATHWAY surfaces only — orthopedics back/neck pain + disc/stenosis/radiculopathy/
    // sciatica/osteoarthritis + neurology neuropathy(/neuropathic pain)/headache(/migraine) +
    // rheumatology inflammatory_arthritis + psychiatry somatic/substance + shared chest_pain/
    // failed_conservative_management all untouched. radiofrequency_ablation SCOPED (facet/medial-
    // branch/denervation); nerve_block / neuromodulation require pain context; SCS/TPI bare;
    // pain-management review scoped. 0 documents, 0 red flags; chronic_pain/neuropathic_pain/
    // fibromyalgia/cancer_pain/opioid_use_disorder/acute_severe_pain stay DATA_ONLY.
    // pain_clinic_referral BEFORE specialist_pain_management_review.
    'pain_clinic_referral', 'specialist_pain_management_review', 'chronic_pain_syndrome',
    'complex_regional_pain_syndrome', 'failed_conservative_pain_management', 'interventional_pain_assessment',
    'epidural_steroid_injection', 'facet_joint_injection', 'medial_branch_block', 'radiofrequency_ablation',
    'nerve_block_pain_management', 'trigger_point_injection', 'spinal_cord_stimulator_assessment',
    'neuromodulation_assessment', 'multidisciplinary_pain_program',
    // L-30 physical_medicine findings (mutually distinct → appended last; NO front-ordering).
    // Rehabilitation-MOTION surfaces only — orthopedics structural MSK/spine + physical_therapy_trial,
    // neurology neuropathy/movement_disorder/cognitive_decline/gait_instability, neurosurgery
    // spinal_cord_compression/traumatic_brain_injury, pain_management functional_restoration_program/
    // pain_rehabilitation, rheumatology inflammatory_arthritis, dermatology pressure_ulcer, vascular
    // amputation_report, shared functional_limitation/reduced_mobility all untouched. Every pattern
    // carries a rehab/physiatry anchor: post_stroke/TBI/SCI REQUIRE "rehabilitation" (never bare
    // diagnosis); spasticity_management scoped; wheelchair_seating matches "wheelchair assessment";
    // 0 documents, 0 red flags; weakness/disability/deconditioning/frailty/spasticity/contracture/
    // gait_disorder/falls_risk/mobility_impairment stay DATA_ONLY. physiatry_referral BEFORE
    // rehabilitation_medicine_review.
    'physiatry_referral', 'rehabilitation_medicine_review', 'functional_rehabilitation_assessment',
    'mobility_rehabilitation_assessment', 'gait_rehabilitation_assessment', 'post_stroke_rehabilitation',
    'traumatic_brain_injury_rehabilitation', 'spinal_cord_injury_rehabilitation', 'amputee_rehabilitation',
    'prosthetic_rehabilitation', 'orthotics_assessment', 'wheelchair_seating_assessment',
    'spasticity_management_review', 'multidisciplinary_rehabilitation_program',
    'deconditioning_rehabilitation_assessment', 'return_to_function_rehabilitation',
    // L-31 emergency_medicine findings (mutually distinct → appended last; NO cross-module
    // front-ordering). ED-referral / emergency-assessment / stabilization-need surfaces only —
    // shared chest_pain/active_bleeding/syncope/neurological_deficit/severe_shortness_of_breath/
    // sepsis_or_systemic_infection/severe_uncontrolled_pain/fever/shortness_of_breath + allergy
    // anaphylaxis + orthopedics acute_injury + pain acute_severe_pain + general_surgery acute_
    // surgical_abdomen + vascular/neurosurgery/cardiothoracic/thoracic/ID emergencies all untouched.
    // Every pattern is "...requiring emergency/urgent/ED assessment"-scoped (never bare symptom);
    // urgent_ed_assessment BEFORE emergency_department_referral (specific before generic ED surface).
    'urgent_ed_assessment', 'emergency_department_referral', 'acute_emergency_assessment',
    'immediate_medical_assessment_required', 'acute_collapse_emergency_assessment',
    'acute_injury_ed_assessment', 'poisoning_or_overdose_assessment',
    // L-32 critical_care findings (mutually distinct → appended last; NO cross-module front-
    // ordering). ICU-level-care / organ-support surfaces only — emergency_medicine ED-handoff +
    // pulmonology respiratory_failure/acute_respiratory_failure + nephrology acute_kidney_injury +
    // cardiology cardiac arrest + ID septic_shock + shared sepsis all untouched. ventilatory_
    // support_requirement scoped to "ventilatory support" (never "respiratory failure");
    // renal_replacement_therapy_icu requires ICU context; post_cardiac_arrest is the review surface.
    'critical_care_review', 'icu_review', 'icu_admission_required', 'hdu_review',
    'organ_support_requirement', 'ventilatory_support_requirement', 'vasopressor_support_requirement',
    'inotrope_support_requirement', 'renal_replacement_therapy_icu', 'post_cardiac_arrest_critical_care_review',
    // L-33 anesthesiology findings (mutually distinct → appended last; NO cross-module front-
    // ordering). Anesthesia plan/risk/support layer only — critical_care ICU support + emergency
    // ED handoff + pain_management epidural/nerve-block + ENT/allergy airway/anaphylaxis +
    // pulmonology sleep_apnea + OB-GYN obstetric disease all untouched. UK/US spelling via
    // an(a)?esthe(sia|tic); difficult_airway_history is history/risk only (not airway management);
    // malignant_hyperthermia_risk is risk/history only; obstetric_anesthesia_review anesthesia-
    // scoped; anesthesiology_consult_requested is a request surface (not a document). 0 docs, 0 red flags.
    'pre_anesthesia_assessment', 'anesthesia_risk_assessment', 'high_risk_anesthesia_review',
    'perioperative_anesthesia_review', 'procedural_sedation_assessment', 'anesthesia_support_required',
    'general_anesthesia_planning', 'regional_anesthesia_planning', 'neuraxial_anesthesia_planning',
    'difficult_airway_history', 'prior_anesthesia_complication', 'malignant_hyperthermia_risk',
    'obstetric_anesthesia_review', 'anesthesiology_consult_requested',
    // L-34A pathology findings (mutually distinct → appended last; NO cross-module front-ordering).
    // Specimen-truth surfaces only — shared pathology_report/abnormal_pathology (inert) + oncology
    // cancer + organ biopsy reports + clinical_lab (L-34B) all untouched. biopsy_result is generic
    // (organ-qualified reports bind organ specialty earlier); margin/grade pathology-scoped;
    // lymphovascular/perineural invasion intrinsic pathology terms; abnormal_pathology_result
    // distinct from shared abnormal_pathology. 0 red flags; cancer-disease/organ-specimen ids not created.
    'biopsy_result', 'specimen_adequacy_issue', 'margin_status', 'tumor_grade_pathology',
    'lymphovascular_invasion_pathology', 'perineural_invasion_pathology', 'dysplasia_on_histology',
    'atypia_on_cytology', 'abnormal_pathology_result', 'pathology_review_requested', 'tissue_diagnosis_required',
    // L-34B clinical_lab findings (mutually distinct → appended last; NO cross-module front-
    // ordering). Measurement-WORKFLOW + genuinely-unowned generic result surfaces only — shared
    // abnormal_lab/complete_blood_count + hep LFT + endo TFT + GI stool + ID culture/micro/sensitivity +
    // neph urinalysis/eGFR + urology urine_culture + hematology coagulation/bilirubin + oncology
    // tumor_marker + pathology specimen all untouched. critical_lab_value is a notification FINDING
    // (not a red flag); owned result categories parked DATA_ONLY. 0 documents, 0 red flags.
    'lab_result_review', 'critical_lab_value', 'repeat_lab_test_required', 'lab_sample_issue',
    'inadequate_lab_sample', 'hemolysed_sample', 'lab_monitoring_required', 'blood_test_result',
    'electrolyte_result', 'renal_function_result', 'inflammatory_marker_result',
    // L-35 palliative_care findings (mutually distinct → appended last; NO cross-module front-
    // ordering). Serious-illness SUPPORT + quality-of-life CO-ORDINATION surfaces only — critical_
    // care goals_of_care/end_of_life_care/do_not_resuscitate_status/treatment_escalation_plan +
    // anesthesiology palliative_sedation + pain_management cancer_pain/palliative_pain + oncology
    // palliative_oncology/cancer_cachexia + physical_medicine frailty + shared docs all untouched.
    // goals_of_care_discussion + advance_care_planning_discussion are DISCUSSION/co-ordination
    // surfaces (distinct from the critical-care order-surface governance concepts). Every pattern is
    // phrase-level + co-ordination-scoped (never bare palliative/hospice/EOL/symptom/care; never
    // "goals of care"/"advance care planning"/"supportive care"/"family meeting"/"symptom management"
    // alone). comfort_care/symptom_burden NOT here (comfort_care DATA_ONLY; symptom_burden shared-
    // reserved). 0 documents, 0 red flags; no medication/sedation/EOL-instruction language.
    'palliative_care_referral', 'palliative_care_review', 'supportive_care_referral', 'serious_illness_support',
    'symptom_burden_review', 'complex_symptom_support', 'quality_of_life_review', 'goals_of_care_discussion',
    'advance_care_planning_discussion', 'family_meeting_palliative_care', 'psychosocial_support_palliative_care',
    'spiritual_support_palliative_care', 'palliative_care_needs_assessment', 'palliative_symptom_support',
    // L-36 pediatrics findings (mutually distinct → appended last; NO cross-module front-ordering).
    // Genuinely-pediatric presentation surfaces only — pulmonology asthma/wheeze + dermatology eczema +
    // ENT otitis_media/recurrent_tonsillitis + gastroenterology reflux_symptoms/constipation + hepatology
    // jaundice + neurology seizure + infectious_disease gastroenteritis + shared fever/sepsis + psychiatry
    // adhd/autism + general_surgery intussusception all untouched. febrile_seizure (shadowed by neurology
    // seizure) + neonatal_jaundice (shadowed by hepatology jaundice) are DATA_ONLY (no front-order). Every
    // pattern is phrase-level (never bare child/infant/growth/feeding/development). 0 LAB, 0 documents.
    'bronchiolitis', 'croup', 'faltering_growth', 'developmental_delay', 'speech_and_language_delay',
    'nocturnal_enuresis', 'kawasaki_disease', 'developmental_dysplasia_of_the_hip',
    // L-37 internal_medicine findings (mutually distinct → appended last; NO cross-module front-
    // ordering). Conservative general-medicine surfaces only — every organ specialty (cardiology/
    // pulmonology/GI/endo/neph/heme/neuro/ID/rheum/onc) + physical_medicine frailty/falls + palliative
    // + psychiatry medication review + shared symptoms all untouched. Multi-word phrases only (never
    // bare internal/medicine/general/complex/medication/systemic/review/admissions); all verified
    // greenfield (findConceptByText → null). 0 LAB, 0 documents, 0 red flags.
    'multimorbidity', 'polypharmacy', 'diagnostic_uncertainty', 'medically_complex_patient',
    'unexplained_systemic_symptoms', 'recurrent_hospital_admissions', 'general_medical_review',
    // L-39 geriatrics findings (mutually distinct → appended last; NO cross-module front-ordering).
    // Older-adult geriatric-assessment / geriatric-syndrome / care-home / vulnerability / deprescribing
    // surfaces only — physical_medicine frailty/falls_risk/deconditioning/reduced_mobility + neurology
    // cognitive_decline + psychiatry capacity_assessment + internal_medicine polypharmacy/multimorbidity +
    // palliative_care advance_care_planning + critical_care goals_of_care + dermatology pressure_ulcer +
    // shared syncope/weight_loss all untouched. INTRA-MODULE order: comprehensive_geriatric_assessment
    // BEFORE geriatric_assessment (so /geriatric assessment/ does not shadow the CGA phrase). All
    // greenfield (findConceptByText → null). 0 LAB, 0 documents, 0 red flags.
    'comprehensive_geriatric_assessment', 'geriatric_assessment', 'geriatric_medicine_review',
    'geriatric_syndrome', 'care_home_resident', 'age_related_vulnerability', 'deprescribing_review',
    // L-40 sleep_medicine findings (mutually distinct → appended last; NO cross-module front-ordering).
    // Sleep-disorder surfaces only — pulmonology asthma/copd/sleep_apnea/snoring/nocturnal_symptoms +
    // ENT obstructive_sleep_apnea + psychiatry insomnia + neurology movement disorders + cardiology +
    // endocrinology obesity + internal_medicine + shared fatigue all untouched (OSA/sleep_apnea/snoring/
    // insomnia conceptIds are owned-but-inert siblings — reused, never claimed LIVE here). Multi-word/
    // distinct phrases (never bare sleep/apnea/oxygen/nocturnal/sleepiness/cpap/pap). All greenfield
    // (findConceptByText → null). 0 LAB, 0 documents, 0 red flags.
    'sleep_disordered_breathing', 'excessive_daytime_sleepiness', 'hypersomnolence', 'narcolepsy',
    'circadian_rhythm_sleep_disorder', 'parasomnia', 'pap_therapy_issue', 'sleep_study_abnormal',
    // L-41 genetics findings (mutually distinct → appended last; NO cross-module front-ordering).
    // Genetics-specific surfaces only — oncology hereditary cancer (brca/lynch/hereditary_cancer DATA_
    // ONLY here) + maternal_fetal prenatal/carrier screening + pediatrics developmental_delay + neurology
    // + cardiology cardiomyopathy (shadows "inherited cardiomyopathy" → DATA_ONLY) + hematology hemoglobin
    // (shadows "hemoglobinopathy" → DATA_ONLY) + pathology molecular_pathology_report + clinical_lab +
    // shared family-history phrases all untouched. Multi-word/distinct phrases (never bare genetic/
    // inherited/variant/mutation/carrier/family/syndrome/chromosome). All greenfield (findConceptByText
    // → null). 0 LAB, 0 documents, 0 red flags.
    'genetic_counselling', 'suspected_genetic_condition', 'inherited_condition', 'known_pathogenic_variant',
    'carrier_status', 'chromosomal_abnormality', 'variant_of_uncertain_significance', 'consanguinity',
    'dysmorphic_features', 'family_history_of_genetic_condition',
    // L-42 occupational_medicine findings (mutually distinct → appended last; NO cross-module front-
    // ordering). Work-related EVALUATION/CONTEXT surfaces only — pulmonology asthma/occupational_exposure/
    // asbestos_exposure + dermatology contact_dermatitis + ENT hearing_loss + orthopedics/physical_medicine
    // MSK/injury + psychiatry stress + infectious_disease BBV + toxicology-conceptual chemical/heavy-metal +
    // shared injury/pain/functional_limitation all untouched (disease/exposure surfaces parked DATA_ONLY).
    // Multi-word phrases (never bare work/occupational/exposure/injury/stress/capacity/fitness/restriction/
    // absence/accommodation). All greenfield (findConceptByText → null). 0 LAB, 0 documents, 0 red flags.
    'occupational_health_assessment', 'occupational_medicine_review', 'work_related_exposure',
    'fitness_for_work_assessment', 'work_capacity_assessment', 'return_to_work_planning',
    'workplace_accommodation_need', 'work_restrictions', 'occupational_disease_assessment', 'sickness_absence_review',
    // L-43 sports_medicine findings (mutually distinct → appended last; NO cross-module front-ordering).
    // Sport/exercise EVALUATION/CONTEXT/return-to-play surfaces only — orthopedics stress_fracture/
    // fracture/sprain/tendon/ligament + cardiology exertional_chest_pain/syncope_with_exertion +
    // pulmonology asthma + neurology concussion + occupational_medicine return_to_work_planning +
    // physical_medicine/shared rehabilitation/functional_limitation all untouched (injury/disease/symptom
    // surfaces parked DATA_ONLY). return-to-(play|sport) is distinct from occ-med return-to-work (earlier
    // in group). Multi-word phrases (never bare sport/athlete/exercise/injury/overuse/training/return/play).
    // All greenfield (findConceptByText → null). 0 LAB, 0 documents, 0 red flags.
    'sports_medicine_review', 'return_to_play_assessment', 'sports_participation_assessment',
    'athletic_overuse_injury', 'training_load_issue', 'athlete_health_assessment',
    // L-44 addiction_medicine findings (mutually distinct → appended last; NO cross-module front-ordering).
    // Addiction EVALUATION/REFERRAL/recovery-context surfaces only — psychiatry substance_use_disorder/
    // alcohol_use_disorder + pain_management opioid_use_disorder + emergency overdose/intoxication/withdrawal +
    // hematology alcohol_exposure + hepatology hepatitis + maternal_fetal/infectious_disease/clinical_lab
    // sequelae/tests all untouched (substance disorders/acute/sequelae/tests parked DATA_ONLY). EVIDENCE-
    // ONLY labels; no use/sourcing/dosing/combining/detox/treatment-protocol content. Multi-word phrases
    // (never bare addiction/substance/drug/alcohol/opioid/dependence/withdrawal/overdose/relapse). All
    // greenfield (findConceptByText → null). 0 LAB, 0 documents, 0 red flags.
    'addiction_medicine_review', 'substance_use_disorder_assessment', 'polysubstance_use',
    'dependence_history', 'addiction_treatment_program_referral',
  ]),
  // RED_FLAG: shared + L-2 neurology + L-3 hematology + L-4 cardiology red flags.
  //           Cardiology patterns AVOID "chest pain"/"angina" so the shared
  //           `chest_pain` (earlier) still owns broad chest-pain labels.
  RED_FLAG: Object.freeze([
    'active_gi_bleeding', 'chest_pain', 'thunderclap_headache', 'status_epilepticus',
    'febrile_neutropenia', 'suspected_acute_leukemia', 'critically_low_platelet_count',
    'suspected_acute_coronary_syndrome', 'acute_decompensated_heart_failure',
    'syncope_with_exertion', 'sustained_ventricular_tachycardia',
    // L-5 pulmonology red flags — patterns avoid "chest pain"; critical_hypoxia
    // before acute_respiratory_failure so "hypoxic respiratory failure" binds hypoxia.
    'critical_hypoxia', 'significant_hemoptysis', 'suspected_pulmonary_embolism', 'acute_respiratory_failure',
    // L-6 gastroenterology + hepatology red flags — patterns avoid GI-bleed terms
    // (shared `active_gi_bleeding` stays earliest and owns those labels).
    'dysphagia_with_weight_loss', 'suspected_colorectal_cancer_red_flags', 'suspected_bowel_obstruction',
    'acute_liver_failure', 'cholangitis', 'decompensated_cirrhosis',
    // L-7 nephrology red flags — patterns avoid bare \baki\b (finding owns it) and
    // bare "potassium"; mutually distinct → appended.
    'severe_hyperkalemia', 'acute_kidney_injury_with_red_flags', 'rapidly_declining_renal_function',
    // L-8 endocrinology red flags — phrase-based; mutually distinct → appended.
    'diabetic_ketoacidosis', 'hyperosmolar_hyperglycemic_state', 'severe_hypoglycemia',
    'adrenal_crisis', 'thyroid_storm', 'severe_hypercalcemia', 'pituitary_apoplexy',
    // L-10 rheumatology red flags — phrase-based; mutually distinct → appended.
    'suspected_giant_cell_arteritis', 'septic_arthritis', 'acute_hot_swollen_joint',
    // L-11 orthopedics trauma red flags — phrase-based; mutually distinct → appended.
    'open_fracture', 'compartment_syndrome', 'neurovascular_compromise', 'cauda_equina_syndrome',
    // L-12 dermatology red flags — phrase-based; mutually distinct → appended.
    'suspected_melanoma', 'stevens_johnson_syndrome', 'toxic_epidermal_necrolysis',
    'necrotizing_skin_infection', 'cellulitis_with_systemic_features',
    // L-13 ENT red flags — phrase-based; word-bounded where collision-prone → appended.
    'sudden_sensorineural_hearing_loss', 'airway_compromise', 'deep_neck_space_infection',
    'mastoiditis', 'orbital_complication_of_sinusitis', 'suspected_head_and_neck_cancer',
    // L-14 ophthalmology red flags — phrase-based; mutually distinct → appended. Neurology
    // acute_vision_loss stays DATA_ONLY/inert (no runtime overlap with sudden_vision_loss).
    // ENT orbital_complication_of_sinusitis owns orbital cellulitis (not duplicated here).
    'sudden_vision_loss', 'retinal_detachment', 'acute_angle_closure_glaucoma',
    'chemical_eye_injury', 'penetrating_eye_injury', 'corneal_ulcer', 'endophthalmitis',
    // L-15 urology red flags (obstructed_infected_kidney MOVED from nephrology + new urologic
    // emergencies; phrase-based; mutually distinct → appended). Corrections 3 & 4 tighten
    // suppressors. acute_urinary_retention phrases stay distinct from orthopedics cauda_equina.
    'obstructed_infected_kidney', 'acute_urinary_retention', 'testicular_torsion',
    'suspected_prostate_cancer', 'suspected_bladder_cancer', 'suspected_testicular_cancer',
    // L-16 obstetrics_gynecology red flags (gyne emergencies + cancer-suspicion; phrase-based;
    // mutually distinct → appended). Corrections 1 & 4 tighten suppressors. ectopic_pregnancy
    // is the only pregnancy concept made LIVE (general OB-GYN owns the early-pregnancy emergency).
    'ovarian_torsion', 'ectopic_pregnancy', 'postmenopausal_bleeding',
    'suspected_ovarian_cancer', 'suspected_endometrial_cancer', 'suspected_cervical_cancer',
    // L-17 oncology emergencies (no organ owns these; phrase-based; mutually distinct →
    // appended). Corrections 2 & 3 tighten suppressors. Organ suspected_*_cancer red flags +
    // hematology febrile_neutropenia stay where they are (not duplicated). MSCC malignant-
    // qualified (neurology generic cord syndrome is inert; orthopedics cauda_equina distinct).
    'superior_vena_cava_syndrome', 'tumor_lysis_syndrome', 'metastatic_spinal_cord_compression',
    // L-18 infectious_disease red flags (CNS infection + endocarditis; phrase-based; no organ
    // owns these → appended). Corrections 2 & 3 tighten meningitis/encephalitis suppressors
    // (vaccine/history/autoimmune/resolved). shared sepsis + hematology febrile_neutropenia
    // reused (not duplicated). infective_endocarditis not bare "endocarditis".
    'suspected_meningitis', 'suspected_encephalitis', 'infective_endocarditis',
    // L-19 allergy_immunology emergencies (no organ owns these; phrase-based → appended).
    // Correction 4: airway_angioedema "no airway compromise" is governance-only. anaphylaxis
    // suppresses history/action-plan/family-history. ENT airway_compromise keeps "stridor".
    'anaphylaxis', 'airway_angioedema',
    // L-21 maternal_fetal emergencies (phrase-based; mutually distinct → appended). preeclampsia
    // + reduced_fetal_movements MOVED from OB-GYN (LIVE). preeclampsia BEFORE eclampsia so
    // "pre-eclampsia" binds preeclampsia (eclampsia guarded — correction 3). OB-GYN ectopic_
    // pregnancy stays earlier (kept). Full suppression sets per correction 3.
    'preeclampsia', 'eclampsia', 'hellp_syndrome', 'placental_abruption', 'reduced_fetal_movements', 'pprom',
    // L-22 reproductive_endocrinology emergency (assisted-reproduction; phrase-based → appended).
    // NON-anchored (L-21 red-flag binding lesson); full suppression (prevention/risk/history/resolved).
    'ovarian_hyperstimulation_syndrome',
    // L-23 vascular_surgery emergencies (phrase-based; NON-anchored → appended). Correction 4:
    // scoped suppressors (no broad "threatened limb"/"palpable pulses"/"ischemia ruled out").
    // DVT stays hematology; stroke/TIA absent (neurology future).
    'acute_limb_ischemia', 'critical_limb_ischemia', 'ruptured_abdominal_aortic_aneurysm',
    // L-24 general_surgery emergencies (phrase-based; NON-anchored → appended). Correction 3:
    // acute_surgical_abdomen matches "acute abdomen" not "abdominal pain". Correction 4: scoped
    // suppressors. Correction 5: US "ischemia" only. peritonitis activates the surface GI parks
    // inert; GI suspected_bowel_obstruction + hepatology cholangitis + vascular limb ischemia kept.
    'peritonitis', 'acute_surgical_abdomen', 'strangulated_hernia', 'perforated_viscus', 'mesenteric_ischemia',
    // L-25 neurosurgery emergencies (phrase-based; NON-anchored → appended LAST). Oncology
    // metastatic_spinal_cord_compression stays EARLIER (malignant-qualified) so "metastatic
    // spinal cord compression" → oncology and generic "spinal cord compression" → neurosurgery.
    // Never bare /bleed/ /h[ae]morrhage/ /shunt/. neurology acute_spinal_cord_syndrome inert.
    'spinal_cord_compression', 'acute_intracranial_hemorrhage', 'vp_shunt_malfunction',
    // L-26 colorectal_surgery emergencies (phrase-based; NON-anchored → appended LAST). Never
    // bare /sepsis/ (shared inert) /perforation/ /obstruction/ /megacolon/. GI suspected_bowel_
    // obstruction + suspected_colorectal_cancer_red_flags + general_surgery perforated_viscus kept.
    'perianal_sepsis', 'obstructing_colorectal_cancer', 'perforated_diverticulitis', 'toxic_megacolon',
    // L-27 thoracic_surgery emergency (phrase-based; NON-anchored → appended LAST). Scoped:
    // empyema_with_sepsis to pleural/thoracic empyema (shared sepsis inert; pulmonology
    // pneumonia_with_sepsis distinct). NOTE: mediastinal_mass_with_airway_compromise is
    // DATA_ONLY (ENT airway_compromise owns the airway-compromise red-flag surface — not grouped).
    'empyema_with_sepsis',
    // L-27B cardiothoracic_surgery emergencies (phrase-based; NON-anchored → appended LAST). Scoped:
    // type_a_aortic_dissection is type-A / ascending / Stanford-A specific (vascular owns Type B /
    // descending / generic dissection — reserved-absent); sternal_wound_with_sepsis to sternal/
    // sternotomy/mediastinitis + sepsis (shared sepsis inert; thoracic empyema_with_sepsis is pleural-
    // specific; ID infective_endocarditis distinct). NO front-ordering required (no earlier red flag
    // owns these phrases). cardiac_tamponade / prosthetic_valve_endocarditis stay DATA_ONLY.
    'type_a_aortic_dissection', 'sternal_wound_with_sepsis',
    // L-31 emergency_medicine emergencies (phrase-based; NON-anchored → appended LAST). Scoped to
    // "...requiring emergency/urgent/ED assessment" (stabilization need, never bare symptom).
    // Verified greenfield/non-colliding: shared sepsis_or_systemic_infection/active_bleeding are
    // pattern-less/inert; shared chest_pain/active_gi_bleeding don't match these phrases; allergy
    // anaphylaxis distinct. NO front-ordering required. major_bleeding uses ha?emorrhage spelling.
    'shock_requiring_emergency_assessment', 'suspected_sepsis_requiring_emergency_assessment',
    'major_bleeding_emergency_assessment',
    // L-32 critical_care emergencies (phrase-based; NON-anchored → appended LAST). Scoped to
    // "...requiring critical care" (ICU-level need, never bare symptom). Verified greenfield/non-
    // colliding (emergency shock/sepsis are "...requiring emergency assessment"; pulmonology owns
    // respiratory failure; ID owns septic_shock; cardiology owns cardiac arrest). NO front-ordering.
    // INTRA-MODULE: septic_shock_requiring_critical_care BEFORE shock_requiring_critical_care
    // (specific before generic — "septic shock requiring critical care" contains the generic phrase).
    'septic_shock_requiring_critical_care', 'shock_requiring_critical_care',
    'multi_organ_failure_requiring_critical_care', 'reduced_consciousness_requiring_critical_care',
    // L-36 pediatrics red flags (phrase-based; NON-anchored → appended LAST; NO cross-module front-
    // ordering). Conservative, genuinely-pediatric, unowned evidence surfaces — shared sepsis +
    // infectious_disease meningitis + allergy anaphylaxis + general_surgery intussusception all
    // untouched. bilious_vomiting_in_infant (/bilious vomiting/) + suspected_non_accidental_injury
    // (/non-accidental injury/) are verified greenfield (no earlier red flag owns these phrases).
    // EVIDENCE RECOGNITION ONLY — no advice/urgency/safeguarding-workflow behavior.
    'bilious_vomiting_in_infant', 'suspected_non_accidental_injury',
  ]),
});

/**
 * First concept (in the given ordered conceptId list) whose recognition patterns
 * match the text, else null. Mirrors the former matchConcept(text, LIST).
 * @param {string} text
 * @param {string[]} conceptIds  ordered candidate conceptIds (precedence)
 * @returns {Object|null}
 */
export const findConceptByText = (text, conceptIds = []) => {
  const s = String(text || '');
  for (const id of conceptIds) {
    const patterns = CONCEPT_RECOGNITION_PATTERNS[id];
    if (Array.isArray(patterns) && patterns.some((re) => re.test(s))) return getConcept(id);
  }
  return null;
};

// Ordered document-type recognition (verbatim from the former DOC_TYPE_LABEL_HINTS).
// Maps attachment label/description text → a canonical EVIDENCE_DOCUMENT_TYPES value.
// First match wins; order is significant.
export const DOCUMENT_TYPE_HINTS = Object.freeze([
  { documentType: 'IMAGING_REPORT',    patterns: [/\bmri\b/i, /\bct\b/i, /x-?ray/i, /ultrasound/i, /imaging/i, /radiograph/i, /sonograph/i] },
  { documentType: 'LAB_RESULT',        patterns: [/\bcbc\b/i, /complete blood count/i, /\bfbc\b/i, /\blabs?\b/i, /laboratory/i, /blood (test|panel|work)/i, /\bbmp\b/i, /\bcmp\b/i, /\bhba1c\b/i] },
  { documentType: 'REFERRAL_LETTER',   patterns: [/referral/i, /referring/i] },
  { documentType: 'DISCHARGE_SUMMARY', patterns: [/discharge/i] },
  { documentType: 'CONSULT_NOTE',      patterns: [/consult/i] },
  { documentType: 'MEDICATION_LIST',   patterns: [/medication/i, /\bmeds?\b/i, /drug list/i, /med list/i] },
  { documentType: 'INSURANCE_CARD',    patterns: [/insurance/i, /payer/i, /member id/i] },
  { documentType: 'CLINICAL_NOTE',     patterns: [/clinic(al)? note/i, /progress note/i, /\bnote\b/i] },
]);

/**
 * Infer a canonical document type from label/description text, or null.
 * Mirrors the former inferDocTypeFromText.
 * @param {string} text
 * @returns {string|null} canonical EVIDENCE_DOCUMENT_TYPES value
 */
export const inferDocumentTypeFromText = (text) => {
  const s = String(text || '');
  for (const hint of DOCUMENT_TYPE_HINTS) {
    if (hint.patterns.some((re) => re.test(s))) return hint.documentType;
  }
  return null;
};
